/**
 * Product card broken-image diagnostic orchestration (ISS-04-093)
 *
 * admin/editor only: when a product card's image fails to load, re-request it
 * to read the actual HTTP status (403 country-block, 404 missing file, etc.)
 * and render it as a badge, since the native <img> "error" event never
 * exposes a status code.
 */

import { productCardImageErrorForm } from "./product-card-image-error-form";
import { ProductCardImageErrorApiService } from "./product-card-image-error-api";
import { ProductCardImageErrorRender } from "./product-card-image-error-render";

class ProductCardImageErrorHandler {
    /**
     * @param wrapper .product-card__image-wrapper carrying data-image-error-src
     * (set by the card's inline onerror once its <img> fails to load).
     */
    public async handleFailedWrapper(wrapper: HTMLElement): Promise<void> {
        const src = wrapper.dataset.imageErrorSrc || "";
        // Clear immediately (not after the await) so a second mutation on the
        // same wrapper (or the initial sweep racing the observer) can't queue
        // the same diagnosis twice.
        delete wrapper.dataset.imageErrorSrc;

        if (!src) {
            return;
        }

        if (!productCardImageErrorForm.isDiagnosable(wrapper)) {
            return;
        }

        const statusLabel = await ProductCardImageErrorApiService.fetchStatusLabel(src);

        // A 2xx status means our own server-side re-fetch of the URL succeeded
        // — i.e. it can't explain why the browser's <img> failed to render it
        // (transient network blip, non-image content served with a 200,
        // decode failure, etc.). Showing e.g. "200" as a diagnostic badge next
        // to a visibly broken image reads as a contradiction rather than a
        // reason, so skip rendering rather than surface an unhelpful label.
        if (/^2\d\d$/.test(statusLabel)) {
            return;
        }

        const badgesContainer = ProductCardImageErrorRender.ensureBadgesContainer(
            wrapper,
            productCardImageErrorForm.getBadgesContainer(wrapper),
        );

        ProductCardImageErrorRender.appendStatusBadge(badgesContainer, statusLabel, src);
    }
}

export { ProductCardImageErrorHandler };
